import React, { lazy, Suspense, useEffect, useState, Fragment } from 'react'
import { withRouter } from 'react-router-dom'
import { Alert, Layout, Spin, Row, Button } from 'antd'
import { useDispatch } from 'react-redux'
import { appSelector } from '../helpers/appSelector'
import { AppDispatch } from '../helpers/appDispatch'
import { exportViewVASOverviewRequest, viewVASOverviewRequest } from '../store/vas-processed'
import { isEmpty } from '../helpers/isEmpty'
import { MerchantData } from '../interfaces'
import { getMerchantsRequest, getVASServiceRequest } from '../store/reports'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

const Filters = lazy(() => import('../components/vas-processed/Filters'))
// const Cards = lazy(() => import('../components/vas-processed/Cards'))
const Details = lazy(() => import('../components/vas-processed/Details'))
const CurrencyFilter = lazy(() => import('../components/vas-processed/CurrencyFilter'))
const EmptyBox = lazy(() => import('../components/vas-processed/EmptyBox'))

const { Content } = Layout

const VASProcessedPage = () => {
  const dispatch: AppDispatch = useDispatch()
  const { t } = useTranslation()
  const vasStore = appSelector((state) => state.vas)
  const reports = appSelector((state) => state.reports)
  const { vas, loading, isExporting, vasError, vasExportError } = vasStore
  const { merchants, vasservices } = reports
  const vasData = vas?.records || vas?.Records || []
  const vasSummary = vas?.summary || vas?.Summary || null
  const vasTotal = vasSummary
    ? vasSummary.recordsTotal ||
      vasSummary.RecordsTotal ||
      vasData.length
    : vasData.length
  const [exportType, setExportType] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [processType, setProcessType] = useState('')
  const [merchant, setMerchant] = useState<MerchantData | null>(null)
  const [currency, setCurrency] = useState('USD')
  const [pageSize, setPageSize] = useState(10)
  const [skip, setSkip] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  const getErrorMessage = (err: any) => {
    if (typeof err === 'string') return err
    return err?.detail || err?.message || err?.title || JSON.stringify(err)
  }

  useEffect(() => {
    dispatch(getMerchantsRequest())
    dispatch(getVASServiceRequest())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const reloadOverviews = () => {
    dispatch(viewVASOverviewRequest({
      currency,
      periodFrom: fromDate,
      periodTo: toDate,
      merchant: merchant?.name || '',
      exportType: '',
      pageSize,
      skip,
      fixedPeriod: 'overall',
      processTypeId: processType
    }))
  }

  const onExportClick = (type: string) => {
    setExportType(type)
    dispatch(exportViewVASOverviewRequest({
      currency,
      periodFrom: fromDate,
      periodTo: toDate,
      merchant: merchant?.name || '',
      exportType: type,
      pageSize,
      skip,
      fixedPeriod: 'overall',
      processTypeId: processType
    }))
  }

  const onLoadMore = (page: number, size: number) => {
    const newSkip = page - 1
    setPageSize(size)
    setSkip(newSkip)
    setCurrentPage(page)

    dispatch(viewVASOverviewRequest({
      currency,
      periodFrom: fromDate,
      periodTo: toDate,
      merchant: merchant?.name || '',
      exportType: '',
      pageSize: size,
      skip: newSkip,
      fixedPeriod: 'overall',
      processTypeId: processType
    }))
  }

  const onReset = (form: any) => {
    form.resetFields()
    setFromDate('')
    setToDate('')
    setMerchant(null)
    setProcessType('')
    setPageSize(10)
    setSkip(0)
    setCurrentPage(1)
    setIsSearching(false)

    dispatch(viewVASOverviewRequest({
      currency,
      periodFrom: '',
      periodTo: '',
      merchant: '',
      exportType: '',
      pageSize: 10,
      skip: 0,
      fixedPeriod: 'overall',
      processTypeId: ''
    }))
  }

  const onSelectCurrency = (value: string) => {
    setCurrency(value)
    setSkip(0)
    setCurrentPage(1)

    dispatch(viewVASOverviewRequest({
      currency: value,
      periodFrom: fromDate,
      periodTo: toDate,
      merchant: merchant?.name || '',
      exportType: '',
      pageSize,
      skip: 0,
      fixedPeriod: 'overall',
      processTypeId: processType
    }))
  }

  const onSearch = (values: any) => {
    setIsSearching(true)
    setSkip(0)
    setCurrentPage(1)
    
    const { periodFrom, periodTo, merchant: merchantId, processTypeId } = values
    let m: MerchantData | undefined = undefined
    let pFrom: string = '',
      pTo: string = ''
      
    if (periodFrom !== undefined && periodTo !== undefined) {
      pFrom = moment(periodFrom).format('MM/DD/YYYY')
      pTo = moment(periodTo).format('MM/DD/YYYY')
      setFromDate(pFrom)
      setToDate(pTo)
    }
    
    if (merchantId !== undefined) {
      m = merchants.find((m) => m.merchantId === merchantId)
      setMerchant(m !== undefined ? m : null)
    } else {
      setMerchant(null)
    }

    setProcessType(processTypeId || '')

    const searchParams = {
      currency,
      periodFrom: pFrom,
      periodTo: pTo,
      merchant: m?.name || '',
      exportType: '',
      pageSize,
      skip: 0,
      fixedPeriod: 'overall',
      processTypeId: processTypeId || ''
    }

    dispatch(viewVASOverviewRequest(searchParams))
  }

  return (
    <div className="padding-box">
      <Content className="site-layout-background site-box">
        <Suspense
          fallback={
            <Row className="suspense-container">
              <div style={{ marginTop: '200px' }}>
                <Spin />
              </div>
            </Row>
          }
        >
          <Fragment>
            <Filters
              onReset={onReset}
              onSearch={onSearch}
              merchants={merchants}
              translate={t}
              vasservices={vasservices}
            />
            {isSearching && vasError && (
              <Alert
                message="Failed to fetch data"
                description={getErrorMessage(vasError)}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            {isSearching && vasExportError && (
              <Alert
                message="Failed to export data"
                description={getErrorMessage(vasExportError)}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            {isSearching ? (
              <Fragment>
                <div className="margin-top">
                  <Row style={{ position: 'relative' }}>
                    <h4 className="transaction-chart-text">{t('general.vasTable')}</h4>
                    <div className="utility-buttons">
                      <>
                        <Button
                          type="primary"
                          className="export-buttons"
                          onClick={() => onExportClick('EXCEL')}
                          loading={isExporting && exportType === 'EXCEL'}
                          disabled={isEmpty(vasData)}
                        >
                          {t('general.export-excel')}
                        </Button>
                      </>
                      <Button
                        type="primary"
                        className="export-buttons reload"
                        onClick={() => reloadOverviews()}
                      >
                        {t('general.refresh')}
                      </Button>
                    </div>
                  </Row>
                  <CurrencyFilter onSelectCurrency={onSelectCurrency} translate={t} />
                  <Details
                    vasprocessedData={vasData}
                    currency={currency}
                    loading={loading}
                    onLoadMore={onLoadMore}
                    total={vasTotal}
                    translate={t}
                    currentPage={currentPage}
                  />
                </div>
              </Fragment>
            ) : (
              <EmptyBox header={t('general.filterToGetData')} description="" />
            )}
          </Fragment>
        </Suspense>
      </Content>
    </div>
  )
}

export default withRouter(VASProcessedPage)
